/**
 * AI 服务
 * 提供通用的 AI 调用功能，供所有应用调用
 *
 * ⚠️ 警告：此服务是前端直接调用 AI API（如阿里云 Qwen API）
 *
 * 安全问题：
 * 1. API Key 会暴露在前端代码中，存在安全风险
 * 2. 浏览器直接调用第三方 API 会遇到 CORS 跨域限制
 * 3. 无法统一管理和监控 AI 调用
 *
 * 推荐做法：
 * 1. 使用后端 API 代理：前端调用后端接口（如 /ai/analyze），后端再调用 AI 服务
 * 2. 使用 useCompsAi Hook：默认通过后端 API 调用，更安全可靠
 *
 * 默认配置：
 * - API Key: sk-79bb1c54a76d4fbba359a4f49060873a（来自 qwenConfig.example.ts）
 * - 模型: qwen-turbo
 * - 端点: https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation
 *
 * @deprecated 建议使用后端 API 调用方式，而不是直接在前端调用 AI 服务
 */
/**
 * AI 服务类
 */
export class AiService {
    /**
     * 在不同构建工具下读取环境变量
     * - CRA: process.env.REACT_APP_*
     * - Next: process.env.NEXT_PUBLIC_*
     * - Vite: import.meta.env.VITE_*
     *
     * 注意：在浏览器生产环境里 `process` 可能不存在，需要做兼容。
     */
    static getEnv(key) {
        // CRA / Next（Node 或部分 bundler 注入）
        const fromProcess = typeof process !== "undefined" &&
            process?.env &&
            process.env[key];
        if (typeof fromProcess === "string" && fromProcess.length > 0) {
            return fromProcess;
        }
        // Vite（浏览器运行时）
        // ⚠️ 注意：CRA/Webpack 会把 `import.meta` 视为语法错误，因此必须避免直接引用。
        try {
            // eslint-disable-next-line no-new-func
            const meta = new Function("return import.meta")();
            const fromImportMeta = meta?.env?.[key];
            if (typeof fromImportMeta === "string" && fromImportMeta.length > 0) {
                return fromImportMeta;
            }
        }
        catch {
            // 非 Vite 环境：忽略
        }
        return undefined;
    }
    /**
     * 获取默认配置（从环境变量或默认值读取）
     * 注意：在生产环境中，建议通过后端 API 调用，而不是直接在前端配置 API Key
     */
    static getDefaultConfig() {
        return {
            endpoint: AiService.getEnv("REACT_APP_AI_ENDPOINT") ||
                AiService.getEnv("NEXT_PUBLIC_AI_ENDPOINT") ||
                AiService.getEnv("VITE_AI_ENDPOINT") ||
                "https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation",
            apiKey: this.DEFAULT_API_KEY,
            model: AiService.getEnv("REACT_APP_AI_MODEL") ||
                AiService.getEnv("NEXT_PUBLIC_AI_MODEL") ||
                AiService.getEnv("VITE_AI_MODEL") ||
                "qwen-turbo",
            useQwen: true,
            qwenParams: {
                temperature: 0.7,
                max_tokens: 2000,
            },
        };
    }
    /**
     * 设置 AI 服务配置
     * 如果不传入配置，将使用默认配置（从环境变量读取）
     */
    static setConfig(config) {
        if (config) {
            this.config = config;
        }
        else {
            // 使用默认配置（从环境变量读取）
            this.config = this.getDefaultConfig();
        }
    }
    /**
     * 获取当前配置
     */
    static getConfig() {
        return this.config;
    }
    /**
     * 初始化服务（使用默认配置）
     * 从环境变量读取配置并初始化服务
     */
    static init() {
        this.setConfig();
    }
    /**
     * 构建 Qwen API 请求体
     */
    static buildQwenRequestBody(params) {
        const { data, thinkingMode, selectedFields } = params;
        const config = this.config;
        // 构建提示词
        const prompt = thinkingMode.prompt ||
            `请以${thinkingMode.label}的方式处理以下数据：`;
        // 格式化数据
        const dataText = typeof data === "string"
            ? data
            : JSON.stringify(data, null, 2);
        // 构建完整的用户消息
        let userMessage = prompt;
        if (selectedFields && selectedFields.length > 0) {
            userMessage += `\n\n选中的字段：${selectedFields.join(", ")}`;
        }
        userMessage += `\n\n数据内容：\n${dataText}`;
        // Qwen API 请求体格式
        return {
            model: config.model || "qwen-turbo",
            input: {
                messages: [
                    {
                        role: "user",
                        content: userMessage,
                    },
                ],
            },
            parameters: {
                temperature: config.qwenParams?.temperature ?? 0.7,
                max_tokens: config.qwenParams?.max_tokens,
                top_p: config.qwenParams?.top_p,
                ...config.qwenParams,
            },
        };
    }
    /**
     * 解析 Qwen API 响应
     */
    static parseQwenResponse(response) {
        console.log("🔍 [AiService] 解析响应:", {
            hasOutput: !!response.output,
            hasChoices: !!response.output?.choices,
            hasText: !!response.output?.text,
            responseKeys: Object.keys(response),
        });
        // Qwen API 响应格式（标准格式）
        if (response.output?.choices?.[0]?.message?.content) {
            return response.output.choices[0].message.content;
        }
        // Qwen API 响应格式（简化格式）
        if (response.output?.text) {
            return response.output.text;
        }
        // 兼容其他可能的响应格式
        if (response.output?.message?.content) {
            return response.output.message.content;
        }
        if (response.content) {
            return response.content;
        }
        if (response.message) {
            return response.message;
        }
        if (response.text) {
            return response.text;
        }
        // 如果无法解析，返回完整的响应以便调试
        console.error("❌ [AiService] 无法解析响应，原始响应:", response);
        throw new Error(`无法解析 Qwen API 响应。响应格式: ${JSON.stringify(response).substring(0, 200)}`);
    }
    /**
     * 调用 AI 接口
     * @param params AI 调用参数
     * @returns AI 响应结果
     */
    static async callAi(params) {
        // 如果未配置，尝试使用默认配置（从环境变量读取）
        if (!this.config) {
            console.warn("⚠️ [AiService] 未配置，尝试使用默认配置（从环境变量读取）");
            this.setConfig();
        }
        if (!this.config) {
            throw new Error("AI 服务未配置，请先调用 AiService.setConfig() 设置配置，或设置环境变量 REACT_APP_AI_API_KEY");
        }
        try {
            const config = this.config;
            const useQwen = config.useQwen !== false; // 默认使用 Qwen
            // 确定 API 端点和请求头
            let endpoint;
            let headers;
            if (useQwen) {
                // 使用 Qwen API
                const qwenEndpoint = config.endpoint ||
                    "https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation";
                headers = {
                    "Content-Type": "application/json",
                    ...(config.apiKey && {
                        Authorization: `Bearer ${config.apiKey}`,
                    }),
                    ...config.headers,
                };
                // 构建 Qwen API 请求体
                const requestBody = config.buildRequestBody
                    ? config.buildRequestBody(params)
                    : this.buildQwenRequestBody(params);
                // 发送请求
                console.log("📤 [AiService] 发送请求到 Qwen API:", {
                    endpoint: qwenEndpoint,
                    model: requestBody.model,
                    hasApiKey: !!config.apiKey,
                });
                let response;
                try {
                    response = await fetch(qwenEndpoint, {
                        method: config.method || "POST",
                        headers,
                        body: JSON.stringify(requestBody),
                    });
                }
                catch (fetchError) {
                    // 捕获网络错误（包括 CORS、连接失败等）
                    console.error("❌ [AiService] Fetch 请求失败:", fetchError);
                    // 提供更友好的错误信息
                    if (fetchError.name === "TypeError" && fetchError.message.includes("fetch")) {
                        throw new Error("网络请求失败。可能原因：\n" +
                            "1. CORS 跨域限制（浏览器无法直接调用阿里云 API）\n" +
                            "2. 网络连接问题\n" +
                            "3. API 端点不可达\n" +
                            "建议：通过后端代理调用 Qwen API，或在开发环境中配置代理。\n" +
                            `原始错误: ${fetchError.message}`);
                    }
                    throw fetchError;
                }
                console.log("📥 [AiService] 收到响应:", {
                    status: response.status,
                    statusText: response.statusText,
                    ok: response.ok,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMessage = errorData.message ||
                        errorData.error?.message ||
                        errorData.code ||
                        `HTTP error! status: ${response.status}`;
                    console.error("❌ [AiService] Qwen API 错误:", {
                        status: response.status,
                        error: errorData,
                        message: errorMessage,
                    });
                    throw new Error(errorMessage);
                }
                const result = await response.json();
                console.log("✅ [AiService] 响应解析成功");
                // 解析响应
                if (config.parseResponse) {
                    return config.parseResponse(result);
                }
                return this.parseQwenResponse(result);
            }
            else {
                // 使用自定义 API
                if (!config.endpoint) {
                    throw new Error("未配置 API 端点");
                }
                endpoint = config.endpoint;
                headers = {
                    "Content-Type": "application/json",
                    ...config.headers,
                };
                // 构建请求体
                const requestBody = config.buildRequestBody
                    ? config.buildRequestBody(params)
                    : (() => {
                        const { data, thinkingMode, selectedFields, ...rest } = params;
                        return {
                            data,
                            thinkingMode: thinkingMode.id,
                            prompt: thinkingMode.prompt ||
                                `请以${thinkingMode.label}的方式处理以下数据：`,
                            selectedFields,
                            ...rest,
                        };
                    })();
                // 发送请求
                const response = await fetch(endpoint, {
                    method: config.method || "POST",
                    headers,
                    body: JSON.stringify(requestBody),
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const result = await response.json();
                // 解析响应
                if (config.parseResponse) {
                    return config.parseResponse(result);
                }
                // 默认解析逻辑
                return (result.content ||
                    result.text ||
                    result.message ||
                    result.data?.message ||
                    JSON.stringify(result));
            }
        }
        catch (error) {
            console.error("❌ [AiService] AI 调用异常:", {
                error,
                message: error.message,
                stack: error.stack,
                config: {
                    endpoint: this.config?.endpoint,
                    useQwen: this.config?.useQwen,
                    hasApiKey: !!this.config?.apiKey,
                },
            });
            let errorMessage = error.message || "AI 调用失败";
            // 如果是网络错误，提供更详细的提示
            if (errorMessage.includes("Failed to fetch") || errorMessage.includes("网络请求失败")) {
                errorMessage =
                    "网络请求失败，无法连接到 Qwen API。\n\n" +
                        "可能的原因：\n" +
                        "1. CORS 跨域限制：浏览器无法直接调用阿里云 API\n" +
                        "2. 网络连接问题：请检查网络连接\n" +
                        "3. API 端点不可达：请检查 endpoint 配置\n\n" +
                        "解决方案：\n" +
                        "1. 通过后端代理：在后端服务器上设置代理端点转发请求\n" +
                        "2. 开发环境：在 webpack/vite 配置中设置代理\n" +
                        "3. 生产环境：在 nginx 配置中添加代理规则\n\n" +
                        `当前 endpoint: ${this.config?.endpoint || "https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation"}`;
            }
            const finalMessage = this.config?.handleError
                ? this.config.handleError(error)
                : errorMessage;
            throw new Error(finalMessage);
        }
    }
    /**
     * 调用 AI 接口（返回完整结果对象）
     */
    static async callAiWithResult(params) {
        try {
            const message = await this.callAi(params);
            return {
                success: true,
                message,
            };
        }
        catch (error) {
            return {
                success: false,
                message: "",
                error: error.message || "AI 调用失败",
            };
        }
    }
}
AiService.config = null;
/**
 * 默认 API Key（用于开发测试）
 * ⚠️ 警告：这是默认配置，仅用于开发环境
 * 生产环境请使用环境变量或后端 API 调用
 *
 * 默认使用 qwenConfig.example.ts 中配置的密钥
 */
AiService.DEFAULT_API_KEY = AiService.getEnv("REACT_APP_AI_API_KEY") ||
    AiService.getEnv("NEXT_PUBLIC_AI_API_KEY") ||
    AiService.getEnv("VITE_AI_API_KEY") ||
    "sk-79bb1c54a76d4fbba359a4f49060873a"; // 默认 API Key（来自 qwenConfig.example.ts）
/**
 * 默认的 AI 调用函数（兼容旧接口）
 * @deprecated 建议使用 AiService.callAi() 或 AiService.callAiWithResult()
 */
export async function callAi(data, thinkingMode, selectedFields) {
    return AiService.callAi({
        data,
        thinkingMode,
        selectedFields,
    });
}
